import React from 'react';
import { Building2, Info, UserCheck, ShieldCheck, Database, Send, Sparkles } from 'lucide-react';
import { Modal } from '../ui/Modal';

interface Props {
  open: boolean;
  onClose: () => void;
  tipo?: 'aluno' | 'responsavel' | 'geral';
}

export function CadastroEscolarInfoModal({ open, onClose, tipo = 'geral' }: Props) {
  if (!open) return null;

  const isAluno = tipo === 'aluno';
  const isResp = tipo === 'responsavel';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title=""
      size="lg"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 4px 12px 4px' }}>
        
        {/* Header com ícone institucional e espaçamento amplo */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          borderBottom: '1px solid #e2e8f0',
          paddingBottom: 16,
          marginBottom: 4,
        }}>
          <div style={{
            width: 50,
            height: 50,
            borderRadius: 12,
            background: 'linear-gradient(135deg, rgba(71, 85, 105, 0.12), rgba(100, 116, 139, 0.2))',
            border: '1px solid rgba(100, 116, 139, 0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#334155',
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}>
            <Building2 size={26} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.01em' }}>
              O que significa Cadastro Escolar?
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: '.84rem', color: '#64748b' }}>
              Entenda por que este registro institucional ainda não possui uma conta de usuário de acesso
            </p>
          </div>
        </div>

        {/* Bloco 1: Registro Institucional */}
        <div style={{
          padding: '14px 16px',
          borderRadius: 10,
          background: 'rgba(241, 245, 249, 0.7)',
          border: '1px solid #e2e8f0',
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
        }}>
          <Info size={20} style={{ color: '#475569', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: '.85rem', color: '#334155', lineHeight: 1.5 }}>
            <strong style={{ color: '#0f172a', display: 'block', marginBottom: 3, fontSize: '.9rem' }}>
              1. Registro Institucional da Escola
            </strong>
            Este registro faz parte do cadastro acadêmico e administrativo da escola (censo escolar, matrícula, histórico e controle de frequência). Ele existe para o gerenciamento pedagógico interno da instituição.
          </div>
        </div>

        {/* Bloco 2: Origem dos Dados (Demonstração / Importações) */}
        <div style={{
          padding: '14px 16px',
          borderRadius: 10,
          background: 'rgba(248, 250, 252, 0.95)',
          border: '1px solid #cbd5e1',
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
        }}>
          <Database size={20} style={{ color: '#0284c7', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: '.85rem', color: '#334155', lineHeight: 1.5 }}>
            <strong style={{ color: '#0369a1', display: 'block', marginBottom: 3, fontSize: '.9rem' }}>
              2. Origem do Cadastro: Importações ou Ambiente de Demonstração
            </strong>
            Registros escolares sem conta de login ocorrem frequentemente por <strong>dados importados de sistemas externos ou planilhas do Censo</strong>, ou por registros gerados para <strong>ambientes de Demonstração e Simulação</strong>. O cadastro acadêmico existe na escola, mas as credenciais de acesso ao aplicativo ainda não foram criadas.
          </div>
        </div>

        {/* Bloco 3: Criação de Usuários apenas por Convite */}
        <div style={{
          padding: '14px 16px',
          borderRadius: 10,
          background: 'rgba(254, 243, 199, 0.45)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
        }}>
          <Send size={20} style={{ color: '#d97706', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: '.85rem', color: '#334155', lineHeight: 1.5 }}>
            <strong style={{ color: '#b45309', display: 'block', marginBottom: 3, fontSize: '.9rem' }}>
              3. Contas de Usuário são Criadas Apenas por Link de Convite
            </strong>
            Usuários com acesso ao sistema ou ao aplicativo <strong>somente são criados quando um link oficial de convite é emitido</strong>. Ao convidar, o sistema cria o usuário autenticado em modo pendente (com prazo de 72 horas para definição de senha) e vincula automaticamente o registro escolar à conta.
          </div>
        </div>

        {/* Bloco 4: Como Conceder Acesso */}
        <div style={{
          padding: '14px 16px',
          borderRadius: 10,
          background: 'rgba(99, 102, 241, 0.07)',
          border: '1px solid rgba(99, 102, 241, 0.25)',
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
        }}>
          <UserCheck size={20} style={{ color: '#4f46e5', flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: '.85rem', color: '#334155', lineHeight: 1.5 }}>
            <strong style={{ color: '#4338ca', display: 'block', marginBottom: 3, fontSize: '.9rem' }}>
              4. Como Conceder o Acesso ao Aplicativo
            </strong>
            {isResp ? (
              'Para liberar o aplicativo dos pais, clique no ícone "+ Convidar" na linha deste responsável. Preencha o e-mail e CPF para que o sistema gere o convite seguro de 72h e mude o status para AGUARDANDO.'
            ) : isAluno ? (
              'Alunos menores acompanham suas atividades pelo aplicativo dos responsáveis cadastrados. Caso a escola queira liberar acesso direto ao estudante, o convite pode ser emitido individualmente.'
            ) : (
              'Para liberar o acesso, utilize a opção de convite de usuário na linha correspondente.'
            )}
          </div>
        </div>

        {/* Rodapé com botão espaçado */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
          <button
            type="button"
            className="btn-primary"
            onClick={onClose}
            style={{ padding: '9px 24px', fontSize: '.88rem', fontWeight: 600, borderRadius: 8 }}
          >
            Entendido
          </button>
        </div>

      </div>
    </Modal>
  );
}
